import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { IRxLater } from "./AppReactions";
import { AppSource } from "./AppSource";

// AppReactPoll - asks `/send` how ONE queued reaction has fared until the receipt says, and hands the
// verdict to the pill (`AppReactions.Settle`). The text send's poll (`AppMain.OnSendPoll`) watches one
// message and drives the line under the box; a reaction has its own badge, so it has its own watch.
//
// ⚠ THE SAME RULES AS THE TEXT POLL: one request at a time (the next is scheduled only when this one
// answers), every 4 s, at most 45 times; a lost connection is "keep waiting"; running out of polls
// is NOT a failure - the reaction stays queued (`AppReactions.GaveUp`). The meaning of each answer is
// `AppSignUpApi.SendVerdictOf`'s, reached through `AppSource.Check`.

/** Whatever waits on one reaction's verdict: the chat's pill (`AppReactions`) or a search hit (`AppSearchReactWatch`). */
export interface IRxPending {
    IsPending(Mid: string): boolean;
    Settle(Mid: string, Sent: boolean, Unconfirmed: boolean, Why: string): void;
    GaveUp(Mid: string): void;
}

export class AppReactPoll {
    public static readonly EveryMs: number = 4000;
    public static readonly MaxPolls: number = 45;

    private Source: AppSource;
    private Reactions: IRxPending;
    private Mid: string;
    private RequestId: string;
    private Timer: IRxLater;
    private Polls: number = 0;

    public constructor(Source: AppSource, Reactions: IRxPending, Mid: string, RequestId: string, Timer: IRxLater) {
        this.Source = Source;
        this.Reactions = Reactions;
        this.Mid = nz(Mid);
        this.RequestId = nz(RequestId);
        this.Timer = Timer;
    }

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.RequestId.length === 0) { this.Reactions.GaveUp(this.Mid); return; }
            this.Timer.Later(AppReactPoll.EveryMs, this.OnTick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactPoll.Start");
        }
    };

    /** One ask. ⚠ Stops quietly once the page no longer waits on this reaction (a replaced frame). */
    public OnTick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Reactions.IsPending(this.Mid) === false) { return; }
            this.Polls = this.Polls + 1;
            this.Source.Check(this.RequestId, this.OnState);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactPoll.OnTick");
        }
    };

    /** ⚠ "Sent" only on the sender's receipt; unconfirmed and refused are final too. */
    public OnState = (Sent: boolean, Final: boolean, Why: string, Unconfirmed: boolean): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the poll chain continues from here, and a
        // silent return would leave the pill faded for ever.
        try {
            if (Sent === true || Unconfirmed === true || Final === true) {
                this.Reactions.Settle(this.Mid, Sent, Unconfirmed, Why);
                return;
            }
            if (this.Polls >= AppReactPoll.MaxPolls) { this.Reactions.GaveUp(this.Mid); return; }
            this.Timer.Later(AppReactPoll.EveryMs, this.OnTick);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppReactPoll.OnState");
        }
    };
}

/** `IRxLater` over the page's own `window` timers. */
export class AppWindowLater implements IRxLater {
    public constructor(private FaultPrefix: string = "AppWindowLater") { }
    public Later = (Ms: number, Fire: () => void): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return window.setTimeout(Fire, Ms);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: central reporting uses the original caller's timer location.
            getCoreUtils().KGlobalErrorHandler(e, this.FaultPrefix + ".Later");
            return 0;
        }
    };

    public Cancel = (Id: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            window.clearTimeout(Id);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: central reporting uses the original caller's timer location.
            getCoreUtils().KGlobalErrorHandler(e, this.FaultPrefix + ".Cancel");
        }
    };
}
